import { useReactiveVar } from '@apollo/client';
import { DirectionalHint } from '@fluentui/react/lib/Callout';
import { FontIcon } from '@fluentui/react/lib/Icon';
import { Label } from '@fluentui/react/lib/Label';
import { Stack } from '@fluentui/react/lib/Stack';
import { TeachingBubble } from '@fluentui/react/lib/TeachingBubble';
import { useTheme } from '@fluentui/react/lib/Theme';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import * as React from 'react';
import {
    currentStepVar,
    CreateTemplateInstanceWizardSteps,
} from '../../../../../../apollo/reactiveVariables/TemplateInstanceVariables';
import { RouteTypes } from '../../../../../../service/gen/templates';
import { getTooltipHostStyles } from '../../../../../../styles/custom/TooltipHostStyles';
import { getAppHost } from '../../../../../../utilities/TeamsUtils';
import { CoachMark, useCoachMarkState } from '../../../../../../utilities/TemplateUtils';
import { getTeachingBubbleStyles } from './TemplateApproverSettings.styles';
import {
    TemplateInstanceApproverSettingsV2,
    TemplateInstanceApproverSettingsV2Props,
} from './TemplateApproverSettingsV2';
import { TemplateSequentialApproverSettings, UI_StepRouteSettingInput } from './TemplateSequentialApproverSettings';

export interface TemplateInstanceApproverSettingsProps extends TemplateInstanceApproverSettingsV2Props {
    onStepRouteSettingChange: (
        callback:
            | UI_StepRouteSettingInput[]
            | ((stepSettings: UI_StepRouteSettingInput[]) => UI_StepRouteSettingInput[])
    ) => void;
}
export const TemplateInstanceApproverSettings: React.FC<TemplateInstanceApproverSettingsV2Props> = ({
    stepRouteSetting,
    onStepRouteSettingChange,
    ...props
}: TemplateInstanceApproverSettingsProps): JSX.Element => {
    const host = getAppHost();
    const loc = host.getLocale();
    const theme = useTheme();
    const currentStep = useReactiveVar(currentStepVar);
    const isSequential = (props.approverRouteType & RouteTypes.StepRoute) === RouteTypes.StepRoute;
    const [showSequentialApprovalEntrySwitchCoachMarkInTemplate, hideSequentialApprovalEntrySwitchCoachMarkInTemplate] =
        useCoachMarkState(CoachMark.SequentialApprovalEntrySwitchCoachMarkInTemplate, true);
    const sequentialCoachmarkEnabled =
        getAppHost().getEcsConfig().MicrosoftTeamApprovals.SequentialApprovalCoachMarkValue;
    return (
        <>
            <Stack>
                <Label>{loc.strings.approver_option_aria_label}</Label>
                <Stack horizontal tokens={{ childrenGap: '12px' }}>
                    <Toggle
                        id='sequential-request-toggle-button-intemplate'
                        checked={isSequential}
                        label={loc.strings.template_settings_sequential_approval_toggle}
                        inlineLabel
                        onChange={(_ev: any, checked: boolean): void => {
                            if (checked && stepRouteSetting.length === 0) {
                                onStepRouteSettingChange([
                                    {
                                        ...props,
                                        order: 1,
                                    },
                                ]);
                            }
                            props.onApproverRouteTypeChange(
                                checked ? RouteTypes.StepRoute : stepRouteSetting[0]?.approverRouteType
                            );
                        }}
                    />
                    {showSequentialApprovalEntrySwitchCoachMarkInTemplate &&
                        sequentialCoachmarkEnabled &&
                        currentStep === CreateTemplateInstanceWizardSteps.WorkflowSettings && (
                            <TeachingBubble
                                calloutProps={{
                                    calloutWidth: 280,
                                    directionalHint: DirectionalHint.bottomAutoEdge,
                                }}
                                target='#sequential-request-toggle-button-intemplate'
                                styles={getTeachingBubbleStyles}
                                onDismiss={hideSequentialApprovalEntrySwitchCoachMarkInTemplate}
                                hasCloseButton>
                                {loc.strings.sequential_approval_coachmark_dialog_text}
                            </TeachingBubble>
                        )}
                    <TooltipHost
                        styles={getTooltipHostStyles(theme, { root: { marginBottom: '4px' } })}
                        content={loc.strings.template_settings_sequential_approval_tips}>
                        <FontIcon iconName='Info' tabIndex={0} />
                    </TooltipHost>
                </Stack>
                {isSequential ? (
                    <TemplateSequentialApproverSettings
                        header={loc.strings.template_settings_sequential_approval_header}
                        items={stepRouteSetting}
                        dataSourceType={props.dataSourceType}
                        dataSourceTeamId={props.dataSourceTeamId}
                        addButtonText={loc.strings.add_another}
                        orderAriaLabel={loc.strings.recipient_list_signing_order_aria_label}
                        removeAriaLabel={loc.strings.recipient_list_remove_recipient_aria_label}
                        emailAriaLabel={loc.strings.recipient_list_email_aria_label}
                        onUpdate={onStepRouteSettingChange}
                    />
                ) : (
                    <TemplateInstanceApproverSettingsV2 {...props} hideLabel />
                )}
            </Stack>
        </>
    );
};
